import { requestTypeStatusColor } from "@utils/ptoHelpers";
import { format, isSameMonth } from "date-fns";
import { useTranslation } from "react-i18next";

interface WorkedHours {
  id: number;
  worker_id: number;
  date: string;
  hours: number;
  timesheet_id?: number;
  is_holiday?: boolean;
  flat_amount?: number;
  gifted_hours?: number;
  created_at?: string;
  updated_at?: string;
}

interface PTORequest {
  id: number;
  worker_id: number;
  start_date: string;
  end_date: string;
  hours_requested: number;
  status: string;
  request_type: string;
  description?: string;
  request_date?: string;
  pto_balance_at_request?: number;
  pto_balance_after?: number;
  status_reason?: string;
}

interface UserCalendarViewProps {
  monthMatrix: Date[][];
  dayEvents: { [key: string]: (WorkedHours | PTORequest)[] };
  currentMonth: Date;
  isLoading: boolean;
  isInitialLoad: boolean;
}

function isPTORequest(event: WorkedHours | PTORequest): event is PTORequest {
  return "start_date" in event && "end_date" in event;
}

function isWorkedHours(event: WorkedHours | PTORequest): event is WorkedHours {
  return "date" in event && !("start_date" in event);
}

export default function UserCalendarView({
  monthMatrix,
  dayEvents,
  currentMonth,
  isLoading,
  isInitialLoad,
}: UserCalendarViewProps) {
  const { t } = useTranslation();

  return (
    <div className="tw-bg-white dark:tw-bg-[#0D1117] tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-1">
      {/* Calendar Grid */}
      {isInitialLoad && isLoading ? (
        <div className="tw-p-8 tw-text-center">{t("userProfile.loading")}</div>
      ) : (
        <div className="tw-grid tw-grid-cols-7 tw-gap-1">
          {/* Day headers */}
          {[
            t("pto.sun"),
            t("pto.mon"),
            t("pto.tue"),
            t("pto.wed"),
            t("pto.thu"),
            t("pto.fri"),
            t("pto.sat"),
          ].map((day) => (
            <div
              key={day}
              className="tw-p-2 tw-text-center tw-font-semibold tw-bg-gray-100 dark:tw-bg-gray-800"
            >
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {monthMatrix.map((week, wi) => (
            <div key={wi} className="tw-contents">
              {week.map((day, di) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const eventsForDay = dayEvents[dateStr] || [];

                // Separate worked hours and PTO requests
                const workedHoursForDay = eventsForDay.filter(isWorkedHours);
                const ptoRequestsForDay = eventsForDay.filter(isPTORequest);
                //   .filter(isPTORequest)
                //   .filter(
                //     (pto) =>
                //       pto.status === "Approved" ||
                //       pto.status === "Balance_Overridden"
                //   );

                // Calculate totals for worked hours
                const totalHoursForDay = workedHoursForDay.reduce(
                  (sum, wh) => sum + wh.hours,
                  0
                );
                const totalFlatAmount = workedHoursForDay.reduce(
                  (sum, wh) => sum + (wh.flat_amount || 0),
                  0
                );
                const totalGiftedHours = workedHoursForDay.reduce(
                  (sum, wh) => sum + (wh.gifted_hours || 0),
                  0
                );
                const hasHoliday = workedHoursForDay.some(
                  (wh) => wh.is_holiday
                );

                // Calculate PTO hours for this day (including Sundays)
                const ptoHoursForDay = ptoRequestsForDay.reduce((sum, pto) => {
                  return sum + pto.hours_requested;
                }, 0);

                const inMonth = isSameMonth(day, currentMonth);
                const isToday =
                  day.toDateString() === new Date().toDateString();

                return (
                  <div
                    key={di}
                    className={`tw-min-h-[85px] tw-p-1 tw-border tw-border-gray-200 dark:tw-border-gray-700 ${
                      inMonth
                        ? "tw-bg-white"
                        : "tw-bg-gray-50 dark:tw-bg-gray-900"
                    } ${isToday ? "tw-ring-2 tw-ring-blue-500" : ""} ${
                      hasHoliday ? "tw-bg-red-50 dark:tw-bg-red-950" : ""
                    } hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 tw-cursor-pointer`}
                  >
                    <div className="tw-text-sm tw-font-semibold tw-mb-1 tw-flex tw-items-center tw-gap-1">
                      {format(day, "d")}
                      {hasHoliday && (
                        <span className="tw-text-red-500">🎉</span>
                      )}
                    </div>
                    {totalHoursForDay > 0 && (
                      <div className="tw-text-xs tw-bg-blue-100 dark:tw-bg-blue-900 tw-text-blue-800 dark:tw-text-blue-200 tw-rounded tw-px-2 tw-py-1 tw-text-center tw-mb-1">
                        {totalHoursForDay.toFixed(2)} {t("userProfile.hrs")}
                      </div>
                    )}
                    {ptoHoursForDay > 0 &&
                      ptoRequestsForDay.map((pto) => {
                        const startStr = String(pto.start_date).slice(0, 10);
                        const endStr = String(pto.end_date).slice(0, 10);
                        const startsHere = startStr === dateStr;
                        const endsHere = endStr === dateStr;
                        const { bg, text } = requestTypeStatusColor(
                          pto.status,
                          pto.request_type
                        );
                        const mainText = pto.description
                          ? pto.description
                          : "PTO";
                        const status = (pto.status || "")
                          .toString()
                          .toLowerCase();
                        const statusText = t(`pto.statuses.${status}`, {
                          defaultValue: pto.status,
                        });
                        const hoursText =
                          typeof pto.hours_requested === "number"
                            ? ` • ${pto.hours_requested}h`
                            : "";
                        const requestTypeText = pto.request_type
                          ? ` (${pto.request_type})`
                          : "";
                        const label = `${mainText}${requestTypeText} - ${statusText}${
                          endsHere ? hoursText : ""
                        }`;

                        return (
                          <div
                            key={`${pto.id}-${dateStr}`}
                            className={`${bg} ${text} tw-text-[10px] tw-leading-tight tw-px-1 tw-py-0.5 tw-cursor-pointer hover:tw-opacity-80 ${
                              startsHere && endsHere
                                ? "tw-rounded"
                                : startsHere
                                ? "tw-rounded-l"
                                : endsHere
                                ? "tw-rounded-r"
                                : ""
                            } tw-overflow-hidden tw-whitespace-nowrap`}
                            title={`${pto.description || "PTO"} • ${
                              pto.status
                            } - Click to view logs`}
                            // onClick={(e) => {
                            //   e.stopPropagation();
                            //   handleRequestClick(pto);
                            // }}
                          >
                            {label}
                          </div>
                        );
                      })}
                    {totalGiftedHours > 0 && (
                      <div className="tw-text-xs tw-bg-green-100 dark:tw-bg-green-900 tw-text-green-800 dark:tw-text-green-200 tw-rounded tw-px-2 tw-py-1 tw-text-center tw-mb-1">
                        🎁 {totalGiftedHours.toFixed(2)} hrs
                      </div>
                    )}
                    {totalFlatAmount > 0 && (
                      <div className="tw-text-xs tw-bg-yellow-100 dark:tw-bg-yellow-900 tw-text-yellow-800 dark:tw-text-yellow-200 tw-rounded tw-px-2 tw-py-1 tw-text-center">
                        💰 ${totalFlatAmount.toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
